/**
 * Lists of content types
 *
 * are used throughout the application
 * especially in client code
 * for filtering to specific types
 *
 */

export const ParentTypes = ['atlas', 'report', 'dash'];

export const ChartTypes = [
  'linechart',
  'areachart',
  'barchart',
  'horizontalbarchart',
  'groupedbarchart',
  'stackedbarchart',
  'donutchart',
  'parliamentchart',
  'piechart',
  'bar-grouped-horizontal',
  'bar-stacked-horizontal',
  'x-plot',
];

export const LegacyChartTypes = [
  'linechart',
  'areachart',
  'barchart',
  'horizontalbarchart',
  'groupedbarchart',
  'stackedbarchart',
  'donutchart',
  'parliamentchart',
  'piechart',
  'bar-grouped-horizontal',
  'bar-stacked-horizontal',
  'x-plot',
];

//creator types for dimensional requirements
export const TwoDimensionalChartTypes = ['barchart', 'horizontalbarchart', 'donutchart', 'parliamentchart', 'piechart'];
//used in chart.manager.ts for deciding if data grouping is done
export const ShouldAggregateTypes = [
  'areachart',
  'linechart',
  'groupedbarchart',
  'stackedbarchart',
  'bar-grouped-horizontal',
  'bar-stacked-horizontal',
  'ttcharts-linechart',
  'x-plot',
];
//used in chart.manager and creator
export const GroupedOrStackedTypes = ['groupedbarchart', 'stackedbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal', 'x-plot'];

export const BarChartTypes = ['barchart', 'horizontalbarchart', 'groupedbarchart', 'stackedbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal'];
export const BarHorizontalTypes = ['horizontalbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal'];
export const BarVerticalTypes = ['barchart', 'groupedbarchart', 'stackedbarchart'];
export const BarGroupedTypes = ['groupedbarchart', 'bar-grouped-horizontal'];
export const BarStackedTypes = ['stackedbarchart', 'bar-stacked-horizontal'];

export const WaterfallTypes = ['barchart', 'horizontalbarchart', 'stackedbarchart', 'bar-stacked-horizontal'];

export const PieTypes = ['donutchart', 'parliamentchart', 'piechart'];

export const DotTypes = ['x-plot'];
//used in format editor cause of legacy reasons - linechart and areachart are our special sorting friends
//and in creator
export const LineChartTypes = ['linechart', 'areachart', 'ttcharts-linechart'];
export const LegacyLineChartTypes = ['linechart', 'areachart'];

// --- New 23° Charts -- //
export const TTChartTypes = ['ttcharts-linechart']; // All 23° Charts
export const TTLineChartTypes = ['ttcharts-linechart']; // Only 23° Chart Line Charts

export const EChartTypes = ['echart', 'echart-sankey'];

//used in creator to decide if dimension requirements exist
export const NoDimensionRequirements = ['map', 'table'];

//types that can exist without data
//very important for content validator
export const NoDataTypes = ['map', 'atlas', 'report', 'dash'];

export const MapTypes = ['choropleth', 'map', 'bubblemap', 'extrudemap', 'multimap'];
export const MapWithDataTypes = ['choropleth', 'bubblemap', 'extrudemap', 'multimap'];
export const TableTypes = ['table'];

// Types whose range can begin on a value other than zero (Visible range excluding zero)
export const AutoRangeTypes = [...DotTypes, 'linechart', 'ttcharts-linechart'];

// Media Files Categories,
// It groups the Media uploaded by the user into Functional Categories
const MediaFilesCategories = {
  profilepicture: {
    id: 'PROFILE_PICTURE',
    label: 'Profile picture',
  },
  contentlogo: {
    id: 'CONTENT_LOGO',
    label: 'Content Logo',
  },
  topicImage: {
    id: 'TOPIC_IMAGE',
    label: 'Topic Image',
  },
  icon: {
    id: 'ICON_IMAGE',
    label: 'Icon',
  },
};

//deciding which color they prefer and how colors are backuped in this group
//in the typeswitch
const ColorGroups = {
  linechart: 'categorical',
  areachart: 'categorical',
  barchart: 'solid',
  horizontalbarchart: 'solid',
  groupedbarchart: 'categorical',
  stackedbarchart: 'categorical',
  donutchart: 'categorical',
  parliamentchart: 'categorical',
  piechart: 'categorical',
  'bar-grouped-horizontal': 'categorical',
  'bar-stacked-horizontal': 'categorical',
  choropleth: 'continuous',
  bubblemap: 'continuous',
  extrudemap: 'continuous',
  map: 'notype',
  table: 'notype',
  'ttcharts-linechart': 'categorical',
  echart: 'notype',
  'echart-sankey': 'notype',
  multimap: 'notype',
  'x-plot': 'categorical',
};

//atm all these types have data associated with them
//shouldnt get in there and change this maybe split this up into more constants
//serverside and client for editor routing?
const VisualsWithDataTypes = [...ChartTypes, ...TableTypes, ...MapWithDataTypes, ...TTChartTypes, ...EChartTypes];
const VisualTypes = [...ChartTypes, ...MapTypes, ...TableTypes, ...TTChartTypes, ...EChartTypes];
const ContentTypes = [...VisualTypes, ...ParentTypes]; //embedable types does not include collection and dataset

//template specific types
const TemplateTypesWithColor = [...ChartTypes, ...MapWithDataTypes, ...TTChartTypes];
const TemplateAbleTypes = ContentTypes;
const TemplateTooltipTypes = [...ChartTypes, ...MapWithDataTypes];
const TemplateInVisualFilterTypes = [...ChartTypes, ...MapWithDataTypes, ...TTChartTypes];
const TemplateBarCharts = [...BarChartTypes];
const TemplateHorizontalTypes = [...BarHorizontalTypes];
const TemplateDonutTypes = [...PieTypes];

//create specific types
//starting with chart legend enabled
//used in datavizwithtooltip
const TypesWithLegend = [
  'linechart',
  'areachart',
  'groupedbarchart',
  'stackedbarchart',
  'bar-grouped-horizontal',
  'bar-stacked-horizontal',
  'donutchart',
  'parliamentchart',
  'piechart',
  'x-plot',
  // 'ttcharts-linechart' <= not included here, because it has a different legend
];
const TypesForSolidColoring = ['barchart', 'horizontalbarchart'];

// Dynamic Height enabled
const TypesWithDynamicHeight = ['horizontalbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal', 'x-plot'];

// Description Utils
const isTimeType = (type: string): boolean => ['areachart', 'linechart'].includes(type);
const isVerticalType = (type: string): boolean => ['barchart', 'groupedbarchart', 'stackedbarchart'].includes(type);
const isHorizontalType = (type: string): boolean => ['horizontalbarchart', 'bar-grouped-horizontal', 'bar-stacked-horizontal', 'x-plot'].includes(type);
const isPieType = (type: string): boolean => ['donutchart', 'parliamentchart', 'piechart'].includes(type);

//list of objects giving more infos about types
const ContentTypeDescriptors = [
  { icon: 'slideshow', label: 'contentTypes.atlas', value: 'atlas' },
  { icon: 'tablet', label: 'contentTypes.report', value: 'report' },
  { icon: 'database-1', label: 'contentTypes.dataset', value: 'dataset' },
  { icon: 'folder', label: 'contentTypes.collection', value: 'collection' },
  { icon: 'globe', label: 'contentTypes.map', value: 'choropleth' },
  { icon: 'bubbles', label: 'contentTypes.bubblemap', value: 'bubblemap' },
  { icon: 'extrude', label: 'contentTypes.extrudemap', value: 'extrudemap' },
  { icon: 'chart-bar', label: 'contentTypes.barChart', value: 'barchart' },
  { icon: 'chart-vert-bar', label: 'contentTypes.horizontalBarChart', value: 'horizontalbarchart' },
  { icon: 'chart-grouped', label: 'contentTypes.groupedBarChart', value: 'groupedbarchart' },
  { icon: 'chart-grouped-horizontal', label: 'contentTypes.horizontalGroupedBarChart', value: 'bar-grouped-horizontal' },
  { icon: 'chart-stacked', label: 'contentTypes.stackedBarChart', value: 'stackedbarchart' },
  { icon: 'chart-stacked-horizontal', label: 'contentTypes.horizontalStackedBarChart', value: 'bar-stacked-horizontal' },
  { icon: 'chart-area', label: 'contentTypes.areaChart', value: 'areachart' },
  { icon: 'chart-line', label: 'contentTypes.lineChart', value: 'linechart' },
  { icon: 'donut-chart', label: 'contentTypes.donutChart', value: 'donutchart' },
  { icon: 'donut-half-chart', label: 'contentTypes.halfDonutChart', value: 'parliamentchart' },
  { icon: 'chart-pie', label: 'contentTypes.pieChart', value: 'piechart' },
  { icon: 'table', label: 'contentTypes.table', value: 'table' },
  { icon: 'folder-shortcut', label: 'contentTypes.shortcut', value: 'shortcut' },
  { icon: 'globe', label: 'contentTypes.blankmap', value: 'map' },
  { icon: 'chart-line', label: 'contentTypes.23chartsLineChart', value: 'ttcharts-linechart' },
  { icon: 'globe', label: 'contentTypes.multiMap', value: 'multimap' },
  { icon: 'dashboard', label: 'contentTypes.dashboard', value: 'dash' },
  { icon: 'sankey', label: 'contentTypes.eChartSankey', value: 'echart-sankey' },
  { icon: 'braille', label: 'contentTypes.xPlot', value: 'x-plot' },
];

//shown in typeswitch and considered for typeswitch
const creatorSupportedTypes = [
  { type: 'choropleth', label: 'Choropleth Map', order: 10 },
  { type: 'multimap', label: 'MultiMap', order: 11 },
  { type: 'barchart', label: 'Barchart', order: 20 },
  { type: 'linechart', label: 'Line Chart', order: 21 },
  { type: 'stackedbarchart', label: 'Stacked Barchart', order: 22 },
  { type: 'groupedbarchart', label: 'Grouped Barchart', order: 23 },
  { type: 'horizontalbarchart', label: 'Horizontal Barchart', order: 24 },
  { type: 'areachart', label: 'Area Chart', order: 30 },
  { type: 'donutchart', label: 'Donut', order: 40 },
  { type: 'parliamentchart', label: 'Parliament', order: 41 },
  { type: 'piechart', label: 'Pie', order: 42 },
  { type: 'bar-grouped-horizontal', label: 'Horizontal Grouped Barchart', order: 50 },
  { type: 'bar-stacked-horizontal', label: 'Horizontal Stacked Barchart', order: 51 },
  { type: 'table', label: 'Table', order: 70 },
  { type: 'bubblemap', label: 'Bubble Map', order: 80 },
  { type: 'extrudemap', label: 'Bubble Map', order: 81 },
  { type: 'ttcharts-linechart', label: '23° Linechart', order: 90 },
  { type: 'echart-sankey', label: 'Sankey', order: 101 },
  { type: 'x-plot', label: 'X Plot', order: 110 },
];

//all content types
const ViewTypes = [
  { type: 'choropleth', label: 'Choropleth Map', order: 1, icon: 'globe' },
  { type: 'areachart', label: 'Area Chart', order: 2, icon: 'chart-area' },
  { type: 'linechart', label: 'Line Chart', order: 3, icon: 'chart-line' },
  { type: 'barchart', label: 'Barchart', order: 4, icon: 'chart-bar' },
  { type: 'horizontalbarchart', label: 'Horizontal Barchart', order: 5, icon: 'chart-vert-bar' },
  { type: 'groupedbarchart', label: 'Grouped Barchart', order: 6, icon: 'chart-grouped' },
  { type: 'stackedbarchart', label: 'Stacked Barchart', order: 7, icon: 'chart-stacked' },
  { type: 'donutchart', label: 'Donut', order: 8, icon: 'donut-chart' },
  { type: 'parliamentchart', label: 'Parliament', order: 9, icon: 'donut-half-chart' },
  { type: 'piechart', label: 'Pie', order: 10, icon: 'chart-pie' },
  { type: 'markers', label: 'Pin Maps', order: 11, icon: 'target' },
  { type: 'flowchoropleth', label: 'Flow Choropleth', order: 12, icon: 'timeline' },
  { type: 'atlas', label: 'Slideshow', order: 13, icon: 'slideshow' },
  { type: 'compare', label: 'Compare', order: 14, icon: 'fullscreen-exit' },
  { type: 'dataset', label: 'Dataset', order: 15, icon: 'dashboard' },
  { type: 'collection', label: 'Collection', order: 16, icon: 'collection' },
  { type: 'report', label: 'Report', order: 17, icon: 'tablet' },
  { type: 'bar-grouped-horizontal', label: 'Horizontal Grouped Barchart', order: 18, icon: 'chart-grouped-horizontal' },
  { type: 'bar-stacked-horizontal', label: 'Horizontal Stacked Barchart', order: 19, icon: 'chart-stacked-horizontal' },
  { type: 'table', label: 'Table', order: 20, icon: 'table' },
  { type: 'shortcut', label: 'Shortcut', order: 21, icon: 'folder-shortcut' },
  { type: 'map', label: 'Map', order: 22, icon: 'globe' },
  { type: 'bubblemap', label: 'Bubble Map', order: 23, icon: 'bubbles' },
  { type: 'extrudemap', label: 'Extrude Map', order: 24, icon: 'extrude' },
  { type: 'ttcharts-linechart', label: '23° Linechart', order: 25, icon: 'chart-line' },
  { type: 'multimap', label: 'Multi Map', order: 26, icon: 'globe' },
  { type: 'dash', label: 'Dashboard', order: 28, icon: 'dashboard' },
  { type: 'echart', label: 'EChart', order: 29, icon: 'sankey' },
  { type: 'echart-sankey', label: 'EChart Sankey', order: 27, icon: 'sankey' },
  { type: 'x-plot', label: 'X Plot', order: 30, icon: 'braille' },
];

/**
 *
 * used for slug generation when saving content
 * has to be unique
 */
const SlugNames = {
  collection: 'folder',
  dataset: 'dataset',
  atlas: 'atlas',
  report: 'report',
  choropleth: 'choro',
  bubblemap: 'bubblemap',
  extrudemap: 'extrudemap',
  map: 'map',
  areachart: 'area',
  linechart: 'line',
  barchart: 'bar-vertical',
  horizontalbarchart: 'bar-horizontal',
  groupedbarchart: 'bar-grouped-vertical',
  stackedbarchart: 'bar-stacked-vertical',
  donutchart: 'donut',
  parliamentchart: 'parl',
  piechart: 'pie',
  'bar-grouped-horizontal': 'bar-grouped-horizontal',
  'bar-stacked-horizontal': 'bar-stacked-horizontal',
  table: 'table',
  shortcut: 'shortc',
  'ttcharts-linechart': 'ttc-linechart',
  'echart-sankey': 'ec-sankey',
  echart: 'echart', // Invalid, should not exists
  multimap: 'multimap',
  dash: 'dash',
  'x-plot': 'x-plot',
};

/**
 *
 * additional constants
 *
 */

//used for migration to set the content default public
const PublicUserIds = [
  '5ca4d82b9a71a2001d8aebe6',
  '5abcb05e12189600392ef18c',
  '5bd6ddf39f545b001db5ee95',
  '5c4ec7ed8dbf97001f9e1d38',
  '5c8f9f21812a15001df3d1d0',
  '5c811011ce002c001c55d32d',
  '5c7e9df39f6525001e510f47',
  '5bcf2adaa76b68001cb72f2f',
  '5bd9d130d30fad001d6cc391',
  '5beacb99a4f5ea001c9d9816',
  '5c94af824b870d001e4cc658',
];

/**
 * mapping for access levels
 */
const AccessLevels = {
  public: 'public',
  unlisted: 'link',
  private: 'private',
};

/**
 * mapping for content status
 */
const ContentStatus = {
  published: 'published',
  draft: 'draft',
  discontinued: 'discontinued',
};

/**
 * response mapping
 */
const ServerStatusCodes = {
  Success: 'OK',
  Error: 'error',
};

//global template that will be applied if
//user has not chosen another template
const DEFAULTTEMPLATEID = '5e1f24a5ce1edd40958ea236';

/**
 * when parsing values in the client this decides the percentage of
 * values that need to be properly parseable values
 * to consider the column a value column
 */
const AutoDetectTypeThreshold = 0.55;

/**
 * Locales we support
 */
const LocaleReferences = [
  {
    locale: 'de-DE',
    language: 'de',
    numberFormat: {
      thousandSeparator: '.',
      decimalSeparator: ',',
    },
    label: {
      de: 'Deutsch - Deutschland',
      en: 'German - Germany',
    },
  },
  {
    locale: 'en-US',
    language: 'en',
    numberFormat: {
      thousandSeparator: ',',
      decimalSeparator: '.',
    },
    label: {
      de: 'Englisch - Vereinigte Staaten',
      en: 'English - United States',
    },
  },
  {
    locale: 'en-GB',
    language: 'en',
    numberFormat: {
      thousandSeparator: ',',
      decimalSeparator: '.',
    },
    label: {
      de: 'Englisch - Großbritannien',
      en: 'English - United Kingdom',
    },
  },
];

export {
  TemplateDonutTypes,
  TypesForSolidColoring,
  TypesWithLegend,
  LocaleReferences,
  DEFAULTTEMPLATEID,
  TemplateHorizontalTypes,
  TemplateBarCharts,
  SlugNames,
  ViewTypes,
  creatorSupportedTypes,
  ContentTypeDescriptors,
  AutoDetectTypeThreshold,
  VisualTypes,
  ContentTypes,
  PublicUserIds,
  AccessLevels,
  ServerStatusCodes,
  VisualsWithDataTypes,
  TemplateAbleTypes,
  TemplateTypesWithColor,
  TemplateTooltipTypes,
  TemplateInVisualFilterTypes,
  ColorGroups,
  ContentStatus,
  MediaFilesCategories,
  TypesWithDynamicHeight,
  isTimeType,
  isVerticalType,
  isHorizontalType,
  isPieType,
};
